import React from "react"
import { ScrollReveal } from "./scroll-reveal"
import { Quote, User } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { Testimonial } from "../../lib/testimonials"
import { Button } from "./button"
import { EventCategory } from "../../lib/analytics"
import { safeTrackEvent } from "../../lib/analytics-utils"

interface TestimonialProps {
  testimonial: Testimonial;
  showViewMore?: boolean;
  className?: string;
  displayFull?: boolean;
}

export function TestimonialCard({ testimonial, showViewMore = true, className = "", displayFull = false }: TestimonialProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const pageName = currentPath === '/' ? 'home' : currentPath.replace(/^\/|\/$/g, '').replace(/\//g, '_');
  
  const handleViewMoreClick = () => {
    safeTrackEvent({
      category: EventCategory.Navigation,
      action: 'click',
      label: `nav_${pageName}_success_stories`,
      value: 1,
      context: {
        sourcePage: currentPath
      }
    });
  };
  
  return (
    <ScrollReveal>
      <div className={`bg-background/95 backdrop-blur-sm rounded-xl p-8 border border-primary/10 shadow-sm ${className}`}>
        <div className="space-y-6">
          <Quote className="w-12 h-12 text-primary/20" strokeWidth={1} />
          <p 
            className="text-lg italic whitespace-pre-line"
            dangerouslySetInnerHTML={{ 
              __html: displayFull && testimonial.fullQuote 
                ? testimonial.fullQuote 
                : testimonial.quote 
            }}
          />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              {testimonial.anonymous ? (
                <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
              ) : (
                <span className="text-primary font-semibold">
                  {testimonial.name && testimonial.name.trim() 
                    ? testimonial.name.split(' ')[0][0] + (testimonial.name.split(' ')[1]?.[0] || '')
                    : testimonial.role.split(' ').map(word => word[0]).join('')}
                </span>
              )}
            </div>
            <div>
              <p className="font-medium">
                {testimonial.anonymous ? testimonial.role : (testimonial.name ? testimonial.name : testimonial.role)}
              </p>
              <p className="text-sm text-muted-foreground">
                {testimonial.anonymous 
                  ? testimonial.company
                  : (testimonial.role && testimonial.name ? testimonial.role + ", " : "") + testimonial.company
                }
              </p>
            </div>
          </div>
          
          {showViewMore && (
            <div className="pt-4 text-center">
              <Button 
                variant="link" 
                asChild 
                size="sm"
                onClick={handleViewMoreClick}
              >
                <Link to="/success-stories">View all success stories →</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </ScrollReveal>
  )
}

interface TestimonialSectionProps {
  title?: string;
  description?: string;
  testimonial: Testimonial;
  showViewMore?: boolean;
  className?: string;
}

export function TestimonialSection({ 
  title = "Client Success", 
  description = "Real outcomes from our AI implementation partnerships",
  testimonial,
  showViewMore = true,
  className = ""
}: TestimonialSectionProps) {
  return (
    <section className={`py-20 relative ${className}`}>
      <div className="container relative space-y-10">
        <ScrollReveal>
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              {title}
            </h2>
            <p className="text-lg text-muted-foreground font-light">
              {description}
            </p>
          </div>
        </ScrollReveal>

        <div className="max-w-3xl mx-auto">
          <TestimonialCard 
            testimonial={testimonial} 
            showViewMore={showViewMore}
          />
        </div>
      </div>
    </section>
  )
}
